/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import ContentWrapper from 'Component/ContentWrapper';
import { HistoryType } from 'Type/Common';
import Html from 'Component/Html';
import Magazine from '../../component/PostCatagories/Magazine';
import Events from '../../component/PostCatagories/Events';
import SpecialOffers from '../../component/PostCatagories/SpecialOffers';
import News from '../../component/PostCatagories/News';
import NewEvents from '../../component/PostCatagories/NewEvents';
import BlogCategories from '../../component/BlogCategories';
import { getQueryParam } from 'Util/Url';
import BlogPostCard from '../../component/BlogPostCard';
import {
    BLOG_CATEGORY_LISTING,
    CATEGORY_DISPLAY_MODE_MAGAZINE,
    CATEGORY_DISPLAY_MODE_SPECIAL_OFFERS,
    CATEGORY_DISPLAY_MODE_EVENTS,
    CATEGORY_DISPLAY_MODE_NEWS,
    CATEGORY_DISPLAY_MODE_NEW_EVENTS
} from './CategoryListing.config';

import './CategoryListing.style';


/** @namespace ScandiPWA/Blog/Component/CategoryListing/Component */
export class CategoryListing extends PureComponent {
    static propTypes = {
        updateBreadcrumbs: PropTypes.func.isRequired,
        updateMeta: PropTypes.func.isRequired,
        history: HistoryType.isRequired,
        setHeaderState: PropTypes.func.isRequired,
        isLoaded: PropTypes.bool,
        pageSize: PropTypes.number.isRequired,
        posts: PropTypes.shape({
            items: PropTypes.array,
            total_pages: PropTypes.number,
            count: PropTypes.number
        }).isRequired,
        categories: PropTypes.shape({
            items: PropTypes.array,
            count: PropTypes.number
        }).isRequired,
        category: PropTypes.string.isRequired,
        loadMore: PropTypes.func.isRequired
    };

    static defaultProps = {
        isLoaded: false
    };

    componentDidMount() {
        this.updateMeta();
        this.setHeaderState();
        this.updateBreadcrumbs();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.updateMeta();
        this.updateBreadcrumbs();
    }

    updateBreadcrumbs() {
        const { updateBreadcrumbs, mfblog_permalink_route, blog_title } = this.props;
        const breadcrumbs = [
            ...this.getCategoryBreadcrumb(),
            {
                url: `/${mfblog_permalink_route}`,
                name: blog_title
            }
        ];

        updateBreadcrumbs(breadcrumbs);
    }

    updateMeta() {
        const {
            updateMeta,
            redirect_code_previous,
            blog_title,
            meta_description,
            category
        } = this.props;

        if (category) {
            updateMeta({
                title: blog_title,
                description: meta_description,
                canonical_url: window.location.href,
                redirect_from: window.location.pathname,
                redirect_code_previous
            });
        } else {
            let _category = this.getCategory();
            updateMeta({
                title: _category.title,
                meta_title: _category.meta_title,
                meta_keywords: _category.meta_keywords,
                meta_description: _category.meta_description,
                canonical_url: window.location.href,
                redirect_from: window.location.pathname,
                redirect_code_previous
            });
        }
    }

    getDefaultTitle() {
        const { title, blog_title } = this.props;

        if (title) {
            return title;
        }

        return blog_title;
    }

    renderTitle() {
        return (
            <h1 block="CategoryListing" elem="Title">
                {this.getCategoryTitle() || this.getDefaultTitle()}
            </h1>
        );
    }

    /**
     * Gets the canonical url for the specific post
     */
    getCanonicalUrl() {
        let canonicalUrl = window.location.pathname;
        let page = getQueryParam('page', window.location);
        if (page) {
            canonicalUrl += `?page=${page}`;
        }
        return canonicalUrl;
    }

    getCategoryTitle() {
        const { category_id: category, categories: { items } } = this.props;
        const { title } = items.find(({ category_id }) => category_id === category) || {};

        return title;
    }

    getCategory() {
        const { category_id: category, categories: { items } } = this.props;
        const result = items.find(({ category_id }) => category_id === category) || {};

        return result;
    }

    getCategoryDescription() {
        const { category, categories: { items } } = this.props;
        const { content } = items.find(({ category_id }) => category_id === category) || {};

        if (!content)
            return null

        return (
            <div className="CategoryListing-Description"><Html content={content} /></div>
        );
    }

    getCategoryBreadcrumb() {
        const { categories: { count }, category } = this.props;
        const title = this.getCategoryTitle() || '';

        if (count && title) {
            return [
                {
                    url: window.location.pathname,
                    name: title
                }
            ];
        }

        if (!count && category) {
            return [
                {
                    name: ''
                }
            ];
        }

        return [];
    }

    setHeaderState() {
        const { setHeaderState, history } = this.props;
        setHeaderState({
            name: BLOG_CATEGORY_LISTING,
            title: 'Blog',
            onBackClick: () => history.goBack()
        });
    }

    renderGrid() {
        const {
            isLoaded,
            posts: { items },
            pageSize
        } = this.props;

        if (isLoaded && items.length) {
            return items.map((post) => (
                <BlogPostCard
                    key={post.post_id}
                    block="BlogPostCard"
                    post={post}
                />
            ));
        }

        if (isLoaded) {
            return (
                <span>
                    {__('No posts in this category')}
                </span>
            );
        }

        return Array.from({ length: pageSize }, (_, i) => (
            <BlogPostCard
                key={i}
                block="BlogPostCard"
                isPlaceholder
            />
        ));
    }

    renderLoadMore() {
        const { posts: { count, total_pages }, pageSize, loadMore } = this.props;

        if (total_pages == 1) {
            return null;
        }

        return (
            <div block="CategoryListing" elem="LoadMoreWrapper">
                <button
                    block="CategoryListing"
                    elem="LoadMoreButton"
                    mix={{ block: 'Button' }}
                    onClick={loadMore}
                >
                    {__('Load More')}
                </button>
            </div>
        );
    }

    render() {
        const { category, categoryObject: { display_mode } } = this.props;

        if (display_mode === undefined) {
            return (
                <div
                    block="CategoryListing"
                    elem="Placeholder"
                />
            );
        }


        switch (display_mode) {
            case CATEGORY_DISPLAY_MODE_MAGAZINE: {
                return <Magazine {...this.props} />;
                break;
            }
            case CATEGORY_DISPLAY_MODE_EVENTS: {
                return <Events {...this.props} />;
                break;
            }
            case CATEGORY_DISPLAY_MODE_SPECIAL_OFFERS: {
                return <SpecialOffers {...this.props} />;
                break;
            }
            case CATEGORY_DISPLAY_MODE_NEWS: {
                return <News {...this.props} />;
                break;
            }
            case CATEGORY_DISPLAY_MODE_NEW_EVENTS: {
                return <NewEvents {...this.props} />;
                break;
            }
            default:
                return (
                    <main block="CategoryListing">
                        <ContentWrapper label="CategoryListing">
                            {this.renderTitle()}
                            {this.getCategoryDescription()}
                            <div block="CategoryListing" elem="ColumnWrapper">
                                <div block="CategoryListing" elem="Sidebar">
                                    <BlogCategories />
                                </div>
                                <div block="CategoryListing" elem="Grid">
                                    {this.renderGrid()}
                                    {this.renderLoadMore()}
                                </div>
                            </div>
                        </ContentWrapper>
                    </main>
                );
        }
    }
}

export default CategoryListing;
